import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { billingApi, type CancelResult } from '@/api/billing';
import { entitlementsApi, type Entitlements } from '@/api/entitlements';
import { ErrorCodes, isApiError } from '@/api/errors';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ErrorState } from '@/components/ErrorState';
import { StatusChip } from '@/components/StatusChip';
import { useLocale } from '@/i18n/useLocale';
import { SubscribeGateContext, useOpenSubscribe } from '@/features/subscribe/subscribeOfferContext';
import { useMe, useMeRefetch } from '@/session/meHooks';
import { formatWhen } from './formatWhen';
import { PriceBookBlock } from './PriceBookBlock';
import { BillingIntervalControl } from './BillingIntervalControl';
import { useBillingProfile } from './billingProfileContext';
import { settingsHref, type SettingsNotify } from './settingsHref';

interface SubscriptionTabProps {
  onNotify: SettingsNotify;
}

/**
 * The subscription's management (TMMAPP-0012 / 0025): the entitlements, the price book, the billing
 * interval, "active until" and Cancel subscription; Renew opens the subscribe dialog
 * (TMMAPP-0116).
 * TMMAPP-0112 folds it into the Settings card's Subscription section (the one place; the tier pill
 * is that section's Plan row) and reads the page's one billing profile.
 */
export function SubscriptionTab({ onNotify: _onNotify }: SubscriptionTabProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const refetchMe = useMeRefetch();
  const navigate = useNavigate();
  const openSubscribe = useOpenSubscribe();
  const gate = useContext(SubscribeGateContext);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [loadError, setLoadError] = useState<unknown>(undefined);
  const [data, setData] = useState<Entitlements | null>(null);
  const { profile: billing, reload: reloadBilling } = useBillingProfile();
  const [busy, setBusy] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelResult, setCancelResult] = useState<CancelResult | null>(null);
  const [cancelPending, setCancelPending] = useState(false);
  const started = useRef(false);

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      setData(await entitlementsApi.get());
      setStatus('ready');
    } catch (error) {
      setLoadError(error);
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void load();
  }, [load]);

  const zone = me.user.timezone ?? 'UTC';
  const showRenew = me.locked || me.tier === 'NONE' || me.tier === 'GUEST';
  // The entry gate let a never-subscribed person into Settings (TMMAPP-0118 C12): they subscribe,
  // there is nothing to renew.
  const neverSubscribed = gate === 'blocking' && !me.locked;

  async function handleCancel() {
    if (busy) return;
    setBusy(true);
    setCancelPending(false);
    setCancelError(null);
    try {
      const result = await billingApi.cancel();
      setCancelResult(result);
      setCancelOpen(false);
      await refetchMe();
    } catch (error) {
      if (isApiError(error) && error.code === ErrorCodes.SUBSCRIPTION_PENDING_ACTION) {
        setCancelPending(true);
        setCancelOpen(false);
      } else {
        setCancelError(t('settings.errorGeneric'));
      }
    } finally {
      setBusy(false);
    }
  }

  if (status === 'loading') {
    return <Typography variant="body2">{t('common.loading')}</Typography>;
  }
  if (status === 'error' || data === null) {
    return (
      <ErrorState
        error={loadError}
        what={t('errors.what.subscription')}
        onRetry={() => {
          started.current = false;
          void load();
        }}
      />
    );
  }

  const prices = data.prices;
  // A locked person's billing is read for the Billing section, never shown here.
  const subscription = me.locked ? undefined : billing?.subscription;
  const periodEnd =
    subscription?.currentPeriodEnd ??
    me.trialEndsOn ??
    subscription?.compedUntil ??
    data.entitlements.find((row) => row.kind === 'SUBSCRIBER')?.endsAt ??
    '';

  return (
    <Stack spacing={3} sx={{ maxWidth: 640 }}>
      {data.entitlements.map((row) => (
        <Box key={`${row.kind}-${row.startsAt}`}>
          <Typography variant="body1">
            {t(`settings.entitlementKind.${row.kind}`)} ·{' '}
            <StatusChip domain="subscription" status={row.status} />
          </Typography>
          {row.sponsorDisplayName && (
            <Typography variant="body2" color="text.secondary">
              {t('settings.sponsoredBy', { name: row.sponsorDisplayName })}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            {t('settings.entitlementWindow', {
              since: formatWhen(row.startsAt, locale, zone),
              until: row.endsAt ? formatWhen(row.endsAt, locale, zone) : t('settings.openEnded'),
            })}
          </Typography>
        </Box>
      ))}
      {prices && <PriceBookBlock prices={prices} tier={me.tier} />}

      {me.locked && <Typography variant="body1">{t('locked.renewCopy')}</Typography>}

      {showRenew && (
        <Stack spacing={2}>
          {!me.locked && me.tier === 'GUEST' && (
            <Typography variant="body1">{t('subscribe.guestNote')}</Typography>
          )}
          <Box>
            <Button
              variant="contained"
              onClick={() => {
                if (me.tier === 'GUEST') {
                  void navigate('/me/own-books', {
                    state: { from: settingsHref('subscription') },
                  });
                  return;
                }
                openSubscribe();
              }}
            >
              {neverSubscribed
                ? t('subscribeDialog.subscribe')
                : me.locked || me.tier === 'NONE'
                  ? t('plan.renew')
                  : t('menu.ownBooks')}
            </Button>
          </Box>
        </Stack>
      )}

      {!showRenew && me.tier === 'SUBSCRIBER' && (
        <Stack spacing={2}>
          {billing && !me.locked && (
            <BillingIntervalControl
              billing={billing}
              ending={
                cancelResult !== null ||
                data.entitlements.some(
                  (row) => row.kind === 'SUBSCRIBER' && row.status !== 'ACTIVE',
                )
              }
              onRefresh={reloadBilling}
            />
          )}
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="body1">
              {t('settings.subscriptionActive', {
                until: periodEnd ? formatWhen(periodEnd, locale, zone) : '—',
              })}
            </Typography>
            {subscription?.origin === 'COMP' && <Chip size="small" label={t('settings.comped')} />}
          </Stack>
          {me.trialEndsOn !== null && (
            <Typography variant="body2" color="text.secondary">
              {t('settings.trialEnds', { on: formatWhen(me.trialEndsOn, locale, zone) })}
            </Typography>
          )}
          {subscription?.compedUntil && (
            <Typography variant="body2" color="text.secondary">
              {t('settings.compedUntil', {
                on: formatWhen(subscription.compedUntil, locale, zone),
              })}
            </Typography>
          )}
          {cancelPending && <Alert severity="warning">{t('settings.cancelPendingAction')}</Alert>}
          {cancelResult && (
            <Alert severity="info">
              {t('settings.cancelResult', {
                endsAt: formatWhen(cancelResult.endsAt, locale, zone),
              })}
            </Alert>
          )}
          <Box>
            <Button
              color="error"
              variant="outlined"
              onClick={() => {
                setCancelError(null);
                setCancelOpen(true);
              }}
            >
              {t('settings.cancel')}
            </Button>
          </Box>
        </Stack>
      )}

      <ConfirmDialog
        open={cancelOpen}
        title={t('settings.cancelTitle')}
        consequence={t('settings.cancelBody', {
          endsAt: periodEnd ? formatWhen(periodEnd, locale, zone) : '—',
        })}
        confirmLabel={t('settings.cancel')}
        cancelLabel={t('settings.keepSubscription')}
        destructive
        busy={busy}
        error={cancelError}
        onConfirm={() => void handleCancel()}
        onCancel={() => setCancelOpen(false)}
      />
    </Stack>
  );
}
